import { ChangeEvent, InputHTMLAttributes, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { debounce } from 'lodash';

import { MagnifyingGlassIcon as SearchIcon } from '@/resources';

type SearchProps = {
  isSidebar?: boolean;
  callback: (query: string) => void;
  testId?: string;
  containerClassName?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export const Search = ({
  isSidebar,
  callback,
  testId,
  containerClassName,
  ...inputProps
}: SearchProps) => {
  const [t] = useTranslation('common');
  const [searchContent, setSearchContent] = useState<string>('');
  const delayedQuery = useCallback(debounce(callback, 500), [callback]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchContent(e.target.value);
    if (inputProps.onChange) {
      inputProps.onChange(e);
    }
  };

  useEffect(() => {
    if (searchContent) {
      delayedQuery(searchContent.substring(0, 100));
    } else if (!searchContent || searchContent.trim().length < 1) {
      delayedQuery('');
    }
  }, [searchContent]);

  return (
    <div
      className={clsx(
        'flex h-10 w-full items-center rounded-[10px] border border-primary-blue bg-white',
        inputProps.disabled && 'border-secondary-blue-gray !bg-primary-gray',
        isSidebar && 'flex-row-reverse !border-secondary-steel-blue pr-2 text-sm',
        containerClassName
      )}>
      {searchContent ? null : (
        <div className='ml-3 size-4 text-primary-black opacity-40'>
          <SearchIcon />
        </div>
      )}
      <input
        {...inputProps}
        data-testid={testId}
        onChange={onChange}
        value={searchContent}
        type='search'
        placeholder={inputProps.placeholder || `${t('search')}...`}
        className={clsx(
          'mx-3 size-full text-sm text-secondary-gray focus:outline-none',
          inputProps.disabled && 'placeholder:text-secondary-blue-gray'
        )}
      />
    </div>
  );
};
